/**
 * GovCostAnalysisRisksAssumptions — bottom panel with risks + assumptions side by side.
 */
import React, { useState } from 'react';
import api from '../../services/api';
import { S, GOV_COLORS, fmtMoney, RISK_SEVERITY_META } from './govShared';

interface Props {
  versionId: string;
  risks: any[];
  assumptions: any[];
  readOnly: boolean;
  onChanged: () => void;
}

const IMPORTANCE_COLORS: Record<string, string> = {
  low: GOV_COLORS.textDim as any,
  medium: GOV_COLORS.info,
  high: GOV_COLORS.danger,
};

export default function GovCostAnalysisRisksAssumptions({ versionId, risks, assumptions, readOnly, onChanged }: Props) {
  const [showNewRisk, setShowNewRisk] = useState(false);
  const [showNewAssumption, setShowNewAssumption] = useState(false);
  const [riskDraft, setRiskDraft] = useState<any>({ severity: 'medium', probability_pct: 30 });
  const [assDraft, setAssDraft] = useState<any>({ importance: 'medium', requires_confirmation: false });

  const createRisk = async () => {
    if (!riskDraft.title || !riskDraft.description) { alert('Title + description required'); return; }
    try {
      await api.post(`/gov/cost-analysis-versions/${versionId}/risks`, riskDraft);
      setShowNewRisk(false);
      setRiskDraft({ severity: 'medium', probability_pct: 30 });
      onChanged();
    } catch (e: any) { alert(e?.response?.data?.error || 'Save failed'); }
  };

  const createAssumption = async () => {
    if (!assDraft.assumption) { alert('Assumption text required'); return; }
    try {
      await api.post(`/gov/cost-analysis-versions/${versionId}/assumptions`, assDraft);
      setShowNewAssumption(false);
      setAssDraft({ importance: 'medium', requires_confirmation: false });
      onChanged();
    } catch (e: any) { alert(e?.response?.data?.error || 'Save failed'); }
  };

  const toggleRiskResolved = async (r: any) => {
    try { await api.patch(`/gov/cost-analysis-risks/${r.id}`, { resolved: !r.resolved }); onChanged(); }
    catch (e: any) { alert(e?.response?.data?.error || 'Save failed'); }
  };

  const toggleAssConfirmed = async (a: any) => {
    try { await api.patch(`/gov/cost-analysis-assumptions/${a.id}`, { confirmed: !a.confirmed }); onChanged(); }
    catch (e: any) { alert(e?.response?.data?.error || 'Save failed'); }
  };

  const deleteRisk = async (id: string) => {
    if (!window.confirm('Delete this risk?')) return;
    try { await api.delete(`/gov/cost-analysis-risks/${id}`); onChanged(); }
    catch (e: any) { alert(e?.response?.data?.error || 'Delete failed'); }
  };

  const deleteAssumption = async (id: string) => {
    if (!window.confirm('Delete this assumption?')) return;
    try { await api.delete(`/gov/cost-analysis-assumptions/${id}`); onChanged(); }
    catch (e: any) { alert(e?.response?.data?.error || 'Delete failed'); }
  };

  const researchItems = assumptions.filter(a => a.category === 'Research Needed');
  const regularAssumptions = assumptions.filter(a => a.category !== 'Research Needed');

  return (
    <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: 16, marginBottom: 16 }}>
      {/* Risks */}
      <div style={S.card}>
        <div style={{ display: 'flex', alignItems: 'center', marginBottom: 14 }}>
          <h3 style={{ ...S.panelTitle, marginBottom: 0 }}>⚠️ Risk Register ({risks.length})</h3>
          <div style={{ flex: 1 }} />
          {!readOnly && <button onClick={() => setShowNewRisk(true)} style={{ ...S.addBtn, padding: '4px 10px', fontSize: 11 }}>+ Add Risk</button>}
        </div>
        {risks.length === 0 ? (
          <div style={{ ...S.empty, padding: 16 }}>No risks identified.</div>
        ) : (
          risks.map(r => {
            const sev = RISK_SEVERITY_META[r.severity] || RISK_SEVERITY_META.medium;
            return (
              <div key={r.id} style={{
                padding: '10px 0',
                borderBottom: `1px solid ${GOV_COLORS.border}`,
                opacity: r.resolved ? 0.5 : 1,
              }}>
                <div style={{ display: 'flex', alignItems: 'flex-start', gap: 8 }}>
                  <div style={{ ...S.chip, background: sev.color + '22', color: sev.color, marginTop: 2 }}>{sev.label}</div>
                  <div style={{ flex: 1, minWidth: 0 }}>
                    <div style={{ color: '#fff', fontSize: 13, fontWeight: 700, textDecoration: r.resolved ? 'line-through' : 'none' }}>
                      {r.title}
                    </div>
                    <div style={{ color: GOV_COLORS.textMuted, fontSize: 12, marginTop: 4 }}>{r.description}</div>
                    {(r.cost_exposure_low || r.cost_exposure_high) && (
                      <div style={{ color: GOV_COLORS.warning, fontSize: 11, marginTop: 4 }}>
                        Exposure: {fmtMoney(r.cost_exposure_low)} – {fmtMoney(r.cost_exposure_high)}
                        {r.probability_pct && ` · ${r.probability_pct}% likely`}
                      </div>
                    )}
                    {r.mitigation && (
                      <div style={{ color: GOV_COLORS.success, fontSize: 11, marginTop: 4 }}>
                        🛡️ {r.mitigation}
                      </div>
                    )}
                  </div>
                  {!readOnly && (
                    <div style={{ display: 'flex', gap: 4 }}>
                      <button onClick={() => toggleRiskResolved(r)} style={{ background: 'transparent', border: `1px solid ${GOV_COLORS.border}`, color: GOV_COLORS.textMuted, borderRadius: 4, padding: '2px 6px', fontSize: 10, cursor: 'pointer' }}>
                        {r.resolved ? 'Reopen' : '✓ Resolve'}
                      </button>
                      <button onClick={() => deleteRisk(r.id)} style={{ background: 'transparent', border: 'none', color: GOV_COLORS.textDim, cursor: 'pointer', fontSize: 14 }}>×</button>
                    </div>
                  )}
                </div>
              </div>
            );
          })
        )}
      </div>

      {/* Assumptions + Research */}
      <div style={S.card}>
        <div style={{ display: 'flex', alignItems: 'center', marginBottom: 14 }}>
          <h3 style={{ ...S.panelTitle, marginBottom: 0 }}>💡 Assumptions ({regularAssumptions.length}){researchItems.length > 0 ? ` · 🔍 ${researchItems.length} to research` : ''}</h3>
          <div style={{ flex: 1 }} />
          {!readOnly && <button onClick={() => setShowNewAssumption(true)} style={{ ...S.addBtn, padding: '4px 10px', fontSize: 11 }}>+ Add</button>}
        </div>

        {researchItems.length > 0 && (
          <div style={{ marginBottom: 12 }}>
            <div style={{ ...S.kvLabel, marginBottom: 6 }}>🔍 Research Needed</div>
            {researchItems.map(a => (
              <div key={a.id} style={{ padding: '8px 10px', background: '#0F1923', borderRadius: 6, marginBottom: 6, borderLeft: `3px solid ${GOV_COLORS.warning}` }}>
                <div style={{ display: 'flex', alignItems: 'flex-start', gap: 8 }}>
                  <div style={{ flex: 1, minWidth: 0, color: GOV_COLORS.text, fontSize: 12, lineHeight: 1.5 }}>
                    {a.assumption?.replace('[RESEARCH] ', '')}
                  </div>
                  {!readOnly && (
                    <div style={{ display: 'flex', gap: 4 }}>
                      <input type="checkbox" checked={a.confirmed} onChange={() => toggleAssConfirmed(a)} title="Mark researched" />
                      <button onClick={() => deleteAssumption(a.id)} style={{ background: 'transparent', border: 'none', color: GOV_COLORS.textDim, cursor: 'pointer' }}>×</button>
                    </div>
                  )}
                </div>
              </div>
            ))}
          </div>
        )}

        {regularAssumptions.length === 0 ? (
          <div style={{ ...S.empty, padding: 16 }}>No assumptions noted.</div>
        ) : (
          regularAssumptions.map(a => (
            <div key={a.id} style={{ padding: '10px 0', borderBottom: `1px solid ${GOV_COLORS.border}` }}>
              <div style={{ display: 'flex', alignItems: 'flex-start', gap: 8 }}>
                <div style={{ ...S.chip, background: (IMPORTANCE_COLORS[a.importance] as any) + '22', color: IMPORTANCE_COLORS[a.importance] as any, marginTop: 2 }}>
                  {a.importance}
                </div>
                <div style={{ flex: 1, minWidth: 0 }}>
                  {a.category && <div style={{ color: GOV_COLORS.textDim, fontSize: 10, textTransform: 'uppercase', letterSpacing: 0.5, marginBottom: 2 }}>{a.category}</div>}
                  <div style={{ color: '#fff', fontSize: 13, textDecoration: a.confirmed ? 'line-through' : 'none' }}>{a.assumption}</div>
                  {a.requires_confirmation && !a.confirmed && (
                    <div style={{ color: GOV_COLORS.warning, fontSize: 11, marginTop: 4 }}>⚠️ Requires confirmation</div>
                  )}
                </div>
                {!readOnly && (
                  <div style={{ display: 'flex', gap: 4 }}>
                    {a.requires_confirmation && (
                      <input type="checkbox" checked={a.confirmed} onChange={() => toggleAssConfirmed(a)} title="Confirmed" />
                    )}
                    <button onClick={() => deleteAssumption(a.id)} style={{ background: 'transparent', border: 'none', color: GOV_COLORS.textDim, cursor: 'pointer' }}>×</button>
                  </div>
                )}
              </div>
            </div>
          ))
        )}
      </div>

      {showNewRisk && (
        <div style={S.modalBackdrop} onClick={() => setShowNewRisk(false)}>
          <div style={S.modal} onClick={e => e.stopPropagation()}>
            <h2 style={S.modalTitle}>+ Add Risk</h2>
            <div style={S.modalRow}>
              <label style={S.modalLabel}>Title *</label>
              <input type="text" value={riskDraft.title || ''} onChange={e => setRiskDraft({ ...riskDraft, title: e.target.value })} style={{ ...S.input, width: '100%' }} />
            </div>
            <div style={S.modalRow}>
              <label style={S.modalLabel}>Description *</label>
              <textarea value={riskDraft.description || ''} onChange={e => setRiskDraft({ ...riskDraft, description: e.target.value })}
                style={{ ...S.input, width: '100%', minHeight: 70, resize: 'vertical' as const }} />
            </div>
            <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: 12, marginBottom: 14 }}>
              <div>
                <label style={S.modalLabel}>Severity</label>
                <select value={riskDraft.severity} onChange={e => setRiskDraft({ ...riskDraft, severity: e.target.value })} style={{ ...S.select, width: '100%' }}>
                  {Object.entries(RISK_SEVERITY_META).map(([k, v]) => <option key={k} value={k}>{v.label}</option>)}
                </select>
              </div>
              <div>
                <label style={S.modalLabel}>Probability %</label>
                <input type="number" value={riskDraft.probability_pct || ''} onChange={e => setRiskDraft({ ...riskDraft, probability_pct: Number(e.target.value) })} style={{ ...S.input, width: '100%' }} />
              </div>
            </div>
            <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: 12, marginBottom: 14 }}>
              <div>
                <label style={S.modalLabel}>Cost Exposure Low $</label>
                <input type="number" value={riskDraft.cost_exposure_low || ''} onChange={e => setRiskDraft({ ...riskDraft, cost_exposure_low: Number(e.target.value) })} style={{ ...S.input, width: '100%' }} />
              </div>
              <div>
                <label style={S.modalLabel}>Cost Exposure High $</label>
                <input type="number" value={riskDraft.cost_exposure_high || ''} onChange={e => setRiskDraft({ ...riskDraft, cost_exposure_high: Number(e.target.value) })} style={{ ...S.input, width: '100%' }} />
              </div>
            </div>
            <div style={S.modalRow}>
              <label style={S.modalLabel}>Mitigation Plan</label>
              <textarea value={riskDraft.mitigation || ''} onChange={e => setRiskDraft({ ...riskDraft, mitigation: e.target.value })}
                style={{ ...S.input, width: '100%', minHeight: 60, resize: 'vertical' as const }} />
            </div>
            <div style={S.modalFooter}>
              <button onClick={() => setShowNewRisk(false)} style={{ ...S.cancelBtn, flex: 1 }}>Cancel</button>
              <button onClick={createRisk} style={{ ...S.addBtn, flex: 2 }}>Add Risk</button>
            </div>
          </div>
        </div>
      )}

      {showNewAssumption && (
        <div style={S.modalBackdrop} onClick={() => setShowNewAssumption(false)}>
          <div style={S.modal} onClick={e => e.stopPropagation()}>
            <h2 style={S.modalTitle}>+ Add Assumption</h2>
            <div style={S.modalRow}>
              <label style={S.modalLabel}>Assumption *</label>
              <textarea value={assDraft.assumption || ''} onChange={e => setAssDraft({ ...assDraft, assumption: e.target.value })}
                style={{ ...S.input, width: '100%', minHeight: 70, resize: 'vertical' as const }}
                placeholder="Assumes 90-day PoP starting Nov 1..." />
            </div>
            <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: 12, marginBottom: 14 }}>
              <div>
                <label style={S.modalLabel}>Category</label>
                <input type="text" value={assDraft.category || ''} onChange={e => setAssDraft({ ...assDraft, category: e.target.value })} style={{ ...S.input, width: '100%' }} placeholder="Schedule, Site, Labor, ..." />
              </div>
              <div>
                <label style={S.modalLabel}>Importance</label>
                <select value={assDraft.importance} onChange={e => setAssDraft({ ...assDraft, importance: e.target.value })} style={{ ...S.select, width: '100%' }}>
                  <option value="low">Low</option>
                  <option value="medium">Medium</option>
                  <option value="high">High</option>
                </select>
              </div>
            </div>
            <label style={{ display: 'flex', alignItems: 'center', gap: 8, color: GOV_COLORS.textMuted, fontSize: 13, marginBottom: 14, cursor: 'pointer' }}>
              <input type="checkbox" checked={!!assDraft.requires_confirmation} onChange={e => setAssDraft({ ...assDraft, requires_confirmation: e.target.checked })} />
              Requires confirmation before finalizing
            </label>
            <div style={S.modalFooter}>
              <button onClick={() => setShowNewAssumption(false)} style={{ ...S.cancelBtn, flex: 1 }}>Cancel</button>
              <button onClick={createAssumption} style={{ ...S.addBtn, flex: 2 }}>Add Assumption</button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
}
